import {
  CreateSaleAttachmentsDto,
  CreateSalesDto,
  SalesEntity,
  SaleAttachmentsEntity,
  UpdateSaleAttachmentsDto,
  UpdateSalesDto
} from '@crm/dto';
import { ApiResponseInterface, ApiResponseStatus, SalesStatus, UserRole } from '@crm/interface';
import { API_RESPONSE, prepareUpdateFields } from '@crm/utils';
import { Injectable, Logger } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { In, Repository } from 'typeorm';

@Injectable()
export class SalesService {
  private readonly logger = new Logger(SalesService.name);

  constructor(
    @InjectRepository(SalesEntity)
    private readonly salesRepo: Repository<SalesEntity>,
    @InjectRepository(SaleAttachmentsEntity)
    private readonly salesAttachmentsRepo: Repository<SaleAttachmentsEntity>,
  ) {}

  // ====== GET ALL ======
  async getAll(): Promise<ApiResponseInterface> {
    try {
      const sales = await this.salesRepo.find({
        relations: ['monitoring', 'created_by', 'updated_by', 'attachments', 'attachments.attachment'],
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.SALES.GET_ALL.CODE,
        message: API_RESPONSE.SUCCESS.SALES.GET_ALL.MESSAGE,
        data: sales,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.SALES.GET_ALL.CODE,
        message: API_RESPONSE.ERROR.SALES.GET_ALL.MESSAGE,
        data: error,
      };
    }
  }

  // ====== GET FOR SUPERVISOR (created by managers and supervisors) ======
  async getForSupervisor(): Promise<ApiResponseInterface> {
    try {
      const sales = await this.salesRepo.find({
        where: { created_by: { role: In([UserRole.MANAGER, UserRole.SUPERVISOR]) } },
        relations: ['monitoring', 'created_by', 'updated_by', 'attachments', 'attachments.attachment'],
      });
      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.SALES.GET_ALL.CODE,
        message: API_RESPONSE.SUCCESS.SALES.GET_ALL.MESSAGE,
        data: sales,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.SALES.GET_ALL.CODE,
        message: API_RESPONSE.ERROR.SALES.GET_ALL.MESSAGE,
        data: error,
      };
    }
  }

  // ====== UPDATE STATUS (management + supervisor only) ======
  async updateStatus(id: string, status: SalesStatus, updatedById?: string): Promise<ApiResponseInterface> {
    try {
      const sale = await this.salesRepo.findOne({ where: { id } });
      if (!sale) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.SALES.UPDATE.CODE,
          message: API_RESPONSE.ERROR.SALES.UPDATE.MESSAGE,
          data: null,
        };
      }
      sale.status = status;
      if (updatedById) sale.updated_by = { id: updatedById } as any;
      await this.salesRepo.save(sale);
      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.SALES.UPDATE.CODE,
        message: 'Sale status updated successfully.',
        data: sale,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.SALES.UPDATE.CODE,
        message: API_RESPONSE.ERROR.SALES.UPDATE.MESSAGE,
        data: error,
      };
    }
  }

  // ====== GET BY USER ID ======
  async getByUserId(userId: string): Promise<ApiResponseInterface> {
    try {
      const sales = await this.salesRepo.find({
        where: { created_by: { id: userId } },
        relations: ['monitoring', 'created_by', 'updated_by', 'attachments', 'attachments.attachment'],
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.SALES.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.SALES.GET_BY_ID.MESSAGE,
        data: sales,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.SALES.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.SALES.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  // ====== GET BY ID ======
  async getById(id: string): Promise<ApiResponseInterface> {
    try {
      const sale = await this.salesRepo.findOne({
        where: { id },
        relations: ['monitoring', 'created_by', 'updated_by', 'attachments', 'attachments.attachment'],
      });

      if (!sale) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.SALES.GET_BY_ID.CODE,
          message: API_RESPONSE.ERROR.SALES.GET_BY_ID.MESSAGE,
          data: null,
        };
      }

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.SALES.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.SALES.GET_BY_ID.MESSAGE,
        data: sale,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.SALES.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.SALES.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  // ====== CREATE SALE ======
  async create(dto: CreateSalesDto): Promise<ApiResponseInterface> {
    try {
      const createData: any = { ...dto };

      // Map relations
      if (dto.created_by_id) createData.created_by = { id: dto.created_by_id };
      if (dto.updated_by_id) createData.updated_by = { id: dto.updated_by_id };
      createData.monitoring = { id: dto.monitoring_id };

      // Remove raw ID fields
      delete createData.created_by_id;
      delete createData.updated_by_id;
      delete createData.monitoring_id;

      const newSale = this.salesRepo.create(createData);
      await this.salesRepo.save(newSale);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.SALES.CREATE.CODE,
        message: API_RESPONSE.SUCCESS.SALES.CREATE.MESSAGE,
        data: newSale,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.SALES.CREATE.CODE,
        message: API_RESPONSE.ERROR.SALES.CREATE.MESSAGE,
        data: error,
      };
    }
  }

  // ====== UPDATE SALE ======
  async update(dto: UpdateSalesDto): Promise<ApiResponseInterface> {
    try {
      const sale = await this.salesRepo.findOne({ where: { id: dto.id } });
      if (!sale) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.SALES.UPDATE.CODE,
          message: API_RESPONSE.ERROR.SALES.UPDATE.MESSAGE,
          data: null,
        };
      }

      const updateData: any = prepareUpdateFields(dto, { updated_by_id: 'updated_by' });

      // Map relation if present
      if (dto.monitoring_id) updateData.monitoring = { id: dto.monitoring_id };

      Object.assign(sale, updateData);
      await this.salesRepo.save(sale);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.SALES.UPDATE.CODE,
        message: API_RESPONSE.SUCCESS.SALES.UPDATE.MESSAGE,
        data: sale,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.SALES.UPDATE.CODE,
        message: API_RESPONSE.ERROR.SALES.UPDATE.MESSAGE,
        data: error,
      };
    }
  }

  // ====== SOFT DELETE SALE ======
  async softDelete(dto: UpdateSalesDto): Promise<ApiResponseInterface> {
    try {
      const sale = await this.salesRepo.findOne({ where: { id: dto.id } });
      if (!sale) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.SALES.DELETE.CODE,
          message: API_RESPONSE.ERROR.SALES.DELETE.MESSAGE,
          data: null,
        };
      }

      sale.status = SalesStatus.DELETED;

      await this.salesRepo.save(sale);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.SALES.DELETE.CODE,
        message: API_RESPONSE.SUCCESS.SALES.DELETE.MESSAGE,
        data: sale,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.SALES.DELETE.CODE,
        message: API_RESPONSE.ERROR.SALES.DELETE.MESSAGE,
        data: error,
      };
    }
  }

  // ====== CREATE SALE ATTACHMENT ======
  async createSalesAttachment(dto: CreateSaleAttachmentsDto): Promise<ApiResponseInterface> {
    try {
      const createData: any = {
        status: dto.status,
        sale: { id: dto.sales_id },
        attachment: { id: dto.attachment_id },
      };

      const newAttachment = this.salesAttachmentsRepo.create(createData);
      await this.salesAttachmentsRepo.save(newAttachment);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.SALES_ATTACHMENT.CREATE.CODE,
        message: API_RESPONSE.SUCCESS.SALES_ATTACHMENT.CREATE.MESSAGE,
        data: newAttachment,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.SALES_ATTACHMENT.CREATE.CODE,
        message: API_RESPONSE.ERROR.SALES_ATTACHMENT.CREATE.MESSAGE,
        data: error,
      };
    }
  }

  // ====== UPDATE SALE ATTACHMENT ======
  async updateSalesAttachment(dto: UpdateSaleAttachmentsDto): Promise<ApiResponseInterface> {
    try {
      const attachment = await this.salesAttachmentsRepo.findOne({
        where: { sale: { id: dto.sales_id }, attachment: { id: dto.attachment_id } },
      });

      if (!attachment) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.SALES_ATTACHMENT.UPDATE.CODE,
          message: API_RESPONSE.ERROR.SALES_ATTACHMENT.UPDATE.MESSAGE,
          data: null,
        };
      }

      attachment.status = dto.status;
      await this.salesAttachmentsRepo.save(attachment);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.SALES_ATTACHMENT.UPDATE.CODE,
        message: API_RESPONSE.SUCCESS.SALES_ATTACHMENT.UPDATE.MESSAGE,
        data: attachment,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.SALES_ATTACHMENT.UPDATE.CODE,
        message: API_RESPONSE.ERROR.SALES_ATTACHMENT.UPDATE.MESSAGE,
        data: error,
      };
    }
  }
}